package co.maytes.standupbot.dashboard.styles

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.percent

private val darkMedia = CSSMediaQuery.Raw("(prefers-color-scheme: dark)")
private val reducedMotion = CSSMediaQuery.Raw(Motion.REDUCED_MOTION_QUERY)
private val noPreferenceMotion = CSSMediaQuery.Raw("(prefers-reduced-motion: no-preference)")
private val always = CSSMediaQuery.Raw("(min-width: 0px)")

private fun InitSilkContext.rule(selector: String, build: StyleScope.() -> Unit) {
    stylesheet.registerStyle(selector) { base { Modifier.styleModifier(build) } }
}

private fun InitSilkContext.mediaRule(
    selector: String,
    query: CSSMediaQuery,
    build: StyleScope.() -> Unit,
) {
    stylesheet.registerStyle(selector) {
        cssRule(query, null) { Modifier.styleModifier(build) }
    }
}

private fun InitSilkContext.themedMediaGuarded(
    selector: String,
    light: StyleScope.() -> Unit,
    dark: StyleScope.() -> Unit,
) {
    rule(selector, light)
    mediaRule("body:not([data-theme=\"light\"]) $selector", darkMedia, dark)
    rule("body[data-theme=\"dark\"] $selector", dark)
}

@InitSilk
fun initSiteKeyframes(ctx: InitSilkContext) {
    ctx.stylesheet.registerKeyframes("sb-pulse") {
        each(0.percent, 100.percent) {
            Modifier.styleModifier { property("box-shadow", "0 0 0 0 rgba(94,106,210,.35)") }
        }
        50.percent {
            Modifier.styleModifier { property("box-shadow", "0 0 0 10px rgba(94,106,210,0)") }
        }
    }

    ctx.stylesheet.registerKeyframes("sb-float") {
        each(0.percent, 100.percent) {
            Modifier.styleModifier { property("transform", "translateY(0) rotate(0)") }
        }
        50.percent {
            Modifier.styleModifier { property("transform", "translateY(-8px) rotate(-0.4deg)") }
        }
    }

    ctx.stylesheet.registerKeyframes("sb-loader-sweep") {
        0.percent {
            Modifier.styleModifier {
                property("left", "-40%")
                property("width", "40%")
            }
        }
        50.percent {
            Modifier.styleModifier {
                property("left", "30%")
                property("width", "55%")
            }
        }
        100.percent {
            Modifier.styleModifier {
                property("left", "100%")
                property("width", "40%")
            }
        }
    }

    ctx.stylesheet.registerKeyframes("sb-accent-pulse") {
        0.percent {
            Modifier.styleModifier { property("box-shadow", "0 0 0 0 rgba(94, 106, 210, 0.50)") }
        }
        100.percent {
            Modifier.styleModifier { property("box-shadow", "0 0 0 28px rgba(94, 106, 210, 0)") }
        }
    }

    ctx.stylesheet.registerKeyframes("sb-enter-fade-up") {
        from {
            Modifier.styleModifier {
                property("opacity", "0")
                property("transform", "translateY(${Motion.ENTER_DISTANCE_PX}px)")
            }
        }
        to {
            Modifier.styleModifier {
                property("opacity", "1")
                property("transform", "translateY(0)")
            }
        }
    }

    ctx.stylesheet.registerKeyframes("sb-enter-fade") {
        from {
            Modifier.styleModifier { property("opacity", "0") }
        }
        to {
            Modifier.styleModifier { property("opacity", "1") }
        }
    }

    ctx.stylesheet.registerKeyframes("sb-fade-out") {
        from {
            Modifier.styleModifier { property("opacity", "1") }
        }
        to {
            Modifier.styleModifier { property("opacity", "0") }
        }
    }

    ctx.stylesheet.registerKeyframes("sb-dialog-out") {
        to {
            Modifier.styleModifier {
                property("opacity", "0")
                property("transform", "scale(${Motion.MODAL_SCALE})")
            }
        }
    }

    ctx.stylesheet.registerKeyframes("sb-check-draw") {
        from {
            Modifier.styleModifier { property("stroke-dashoffset", "${Motion.CHECK_DASH_LENGTH}") }
        }
        to {
            Modifier.styleModifier { property("stroke-dashoffset", "0") }
        }
    }

    ctx.stylesheet.registerKeyframes("sb-fade-pulse") {
        each(0.percent, 100.percent) {
            Modifier.styleModifier { property("opacity", "1") }
        }
        50.percent {
            Modifier.styleModifier { property("opacity", "0.55") }
        }
    }
}

@InitSilk
fun initSiteCssRules(ctx: InitSilkContext) {
    ctx.registerReducedMotionGuard()
    ctx.registerGradientAndNav()
    ctx.registerThemeAwareUtilities()
    ctx.registerSlackMockup()
    ctx.registerGridsAndLayout()
    ctx.registerAtmosphere()
    ctx.registerMotionAndTypography()
    ctx.registerPresenceSurfaces()
    ctx.registerPressAndFeedback()
}

private fun InitSilkContext.registerReducedMotionGuard() {
    val keepOnlyFeedbackTransitions: StyleScope.() -> Unit = {
        property("transition-property", "opacity, color, background-color, border-color, box-shadow !important")
        property("transition-duration", "${Motion.DUR_FAST} !important")
        property("scroll-behavior", "auto !important")
    }
    reducedMotionRule("*", keepOnlyFeedbackTransitions)
    reducedMotionRule("*::before", keepOnlyFeedbackTransitions)
    reducedMotionRule("*::after", keepOnlyFeedbackTransitions)

    fadeInsteadOfMove(".sb-enter-fade-up", "sb-enter-fade")
    fadeInsteadOfMove(".sb-content-swap", "sb-enter-fade")
    fadeInsteadOfMove(".sb-state-swap", "sb-enter-fade")
    fadeInsteadOfMove(".sb-ghost-out", "sb-fade-out")
    fadeInsteadOfMove(".sb-ghost-out .sb-dialog", "sb-fade-out")

    dropMovement(".sb-pop", ".sb-dialog", ".sb-tip", ".sb-icon-swap")
    reducedMotionRule(".sb-toast") { property("transform", "translate(-50%, 0) !important") }
    reducedMotionRule(".sb-drawer") {
        property("transform", "none !important")
        property("opacity", "0")
    }
    reducedMotionRule(".sb-drawer.${Motion.OPEN_CLASS}") { property("opacity", "1") }
    reducedMotionRule(".sb-check-draw path") {
        property("animation", "none !important")
        property("stroke-dashoffset", "0 !important")
    }

    stopLoop(".sb-float")
    stopLoop(".sb-pulse")
    stopLoop(".sb-accent-pulse")

    reducedMotionRule(".sb-loader-bar::after") {
        property("animation-name", "sb-fade-pulse !important")
        property("animation-duration", "1.2s !important")
        property("animation-timing-function", "ease-in-out !important")
    }
}

internal fun InitSilkContext.reducedMotionRule(selector: String, build: StyleScope.() -> Unit) {
    mediaRule(selector, reducedMotion, build)
    mediaRule("html.${Motion.FORCE_REDUCED_MOTION_CLASS} $selector", always, build)
}

internal fun InitSilkContext.fadeInsteadOfMove(selector: String, opacityKeyframes: String) =
    reducedMotionRule(selector) {
        property("animation-name", "$opacityKeyframes !important")
        property("animation-duration", "${Motion.DUR_FAST} !important")
        property("animation-delay", "0ms !important")
    }

private fun InitSilkContext.stopLoop(selector: String) =
    reducedMotionRule(selector) { property("animation", "none !important") }

private fun InitSilkContext.dropMovement(vararg selectors: String) =
    selectors.forEach { selector ->
        reducedMotionRule(selector) { property("transform", "none !important") }
    }

private fun InitSilkContext.registerGradientAndNav() {
    rule(".sb-gradient-text") {
        property("background-image", "linear-gradient(40deg, #09090B 20%, #5E6AD2 100%)")
        property("-webkit-background-clip", "text")
        property("background-clip", "text")
        property("color", "transparent")
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-gradient-text", darkMedia) {
        property("background-image", "linear-gradient(40deg, #FAFAFA 20%, #7C88E3 100%)")
    }
    rule("body[data-theme=\"dark\"] .sb-gradient-text") {
        property("background-image", "linear-gradient(40deg, #FAFAFA 20%, #7C88E3 100%)")
    }

    rule(".sb-hero-halo") {
        property("background", "radial-gradient(ellipse at center, rgba(94,106,210,.14), transparent 60%)")
    }

    rule(".sb-link-underline") {
        property("position", "relative")
    }
    rule(".sb-link-underline::after") {
        property("content", "''")
        property("position", "absolute")
        property("left", "0")
        property("bottom", "-2px")
        property("width", "100%")
        property("height", "1px")
        property("background-color", "currentColor")
        property("transform", "scaleX(0)")
        property("transform-origin", "left center")
        property("transition", "transform 180ms cubic-bezier(0.22, 0.61, 0.36, 1)")
    }
    mediaRule(".sb-link-underline:hover::after", HoverFinePointerMedia) {
        property("transform", "scaleX(1)")
    }

    rule(".sb-cta-arrow") {
        property("display", "inline-block")
        property("transition", "transform 200ms cubic-bezier(0.22, 0.61, 0.36, 1)")
    }
    mediaRule(".sb-cta:hover .sb-cta-arrow", HoverFinePointerMedia) {
        property("transform", "translateX(3px)")
    }

    rule(".sb-reveal-hidden") {
        property("opacity", "0")
        property("transform", "translateY(10px)")
    }

    rule(".sb-nav") {
        property("background-color", "rgba(250, 250, 250, 0.72)")
        property("border-bottom", "1px solid #F1F1F3")
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-nav", darkMedia) {
        property("background-color", "rgba(10, 10, 10, 0.72)")
        property("border-bottom-color", "#1F1F22")
    }
    rule("body[data-theme=\"dark\"] .sb-nav") {
        property("background-color", "rgba(10, 10, 10, 0.72)")
        property("border-bottom-color", "#1F1F22")
    }
}

private fun InitSilkContext.registerThemeAwareUtilities() {
    themedMediaGuarded(
        ".sb-icon-plate",
        light = {
            property("width", "48px")
            property("height", "48px")
            property("border-radius", "8px")
            property("background-color", "#F3F3F7")
            property("display", "inline-flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("color", "#5E6AD2")
        },
        dark = {
            property("background-color", "rgba(94, 106, 210, 0.12)")
        },
    )

    mediaRule(".sb-surface", always) {
        property("background-color", "var(--sb-surface, #FFFFFF)")
        property("border-color", "var(--sb-border-subtle, #F1F1F3) !important")
    }

    themedMediaGuarded(
        ".sb-divider-top",
        light = {
            property("border-top", "1px solid #F1F1F3")
        },
        dark = {
            property("border-top-color", "#1F1F22")
        },
    )

    themedMediaGuarded(
        ".sb-text-primary",
        light = { property("color", "#09090B") },
        dark = { property("color", "#FAFAFA") },
    )
    themedMediaGuarded(
        ".sb-text-secondary",
        light = { property("color", "#52525B") },
        dark = { property("color", "#A1A1AA") },
    )

    rule(".sb-theme-icon") {
        property("position", "absolute")
        property("top", "50%")
        property("left", "50%")
        property("transform", "translate(-50%, -50%) scale(1)")
        property("opacity", "0")
        property("pointer-events", "none")
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("line-height", "0")
    }
    mediaRule(".sb-theme-icon", noPreferenceMotion) {
        property(
            "transition",
            "opacity 250ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 250ms cubic-bezier(0.22, 0.61, 0.36, 1)",
        )
    }
    rule(".sb-theme-icon.sb-theme-moon") {
        property("opacity", "1")
    }
    rule(".sb-theme-icon.sb-theme-sun") {
        property("opacity", "0")
        property("transform", "translate(-50%, -50%) scale(0.8)")
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-theme-icon.sb-theme-moon", darkMedia) {
        property("opacity", "0")
        property("transform", "translate(-50%, -50%) scale(0.8)")
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-theme-icon.sb-theme-sun", darkMedia) {
        property("opacity", "1")
        property("transform", "translate(-50%, -50%) scale(1)")
    }
    rule("body[data-theme=\"dark\"] .sb-theme-icon.sb-theme-moon") {
        property("opacity", "0")
        property("transform", "translate(-50%, -50%) scale(0.8)")
    }
    rule("body[data-theme=\"dark\"] .sb-theme-icon.sb-theme-sun") {
        property("opacity", "1")
        property("transform", "translate(-50%, -50%) scale(1)")
    }

    themedMediaGuarded(
        ".sb-menu-item:hover",
        light = { property("background-color", "#F4F4F5") },
        dark = { property("background-color", "#1F1F22") },
    )

    themedMediaGuarded(
        ".sb-connected-tile",
        light = {
            property("display", "flex")
            property("align-items", "center")
            property("gap", "12px")
            property("padding", "14px 18px")
            property("border-radius", "12px")
            property("background-color", "rgba(16, 185, 129, 0.08)")
            property("border", "1px solid rgba(16, 185, 129, 0.25)")
            property("color", "#065F46")
        },
        dark = {
            property("background-color", "rgba(16, 185, 129, 0.10)")
            property("border-color", "rgba(16, 185, 129, 0.30)")
            property("color", "#6EE7B7")
        },
    )
    rule(".sb-connected-tile-icon") {
        property("flex-shrink", "0")
        property("width", "24px")
        property("height", "24px")
        property("border-radius", "9999px")
        property("background-color", "#10B981")
        property("color", "#FFFFFF")
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("font-size", "13px")
        property("font-weight", "700")
    }

    rule(".sb-section-index") {
        property("font-family", "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace")
        property("font-size", "11px")
        property("font-weight", "500")
        property("letter-spacing", "0.12em")
        property("text-transform", "uppercase")
        property("color", "#5E6AD2")
    }

    rule(".sb-ring") {
        property("position", "relative")
        property("border-radius", "16px")
        property("padding", "1px")
        property(
            "background",
            "conic-gradient( from 140deg, rgba(94,106,210,0.55), rgba(94,106,210,0.0) 40%, rgba(94,106,210,0.0) 60%, rgba(94,106,210,0.55) )",
        )
    }
    rule(".sb-ring > *") {
        property("border-radius", "15px")
        property("background-color", "var(--sb-surface, #FFFFFF)")
    }

    themedMediaGuarded(
        ".sb-step-counter",
        light = {
            property("font-family", "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace")
            property("font-size", "11px")
            property("font-weight", "500")
            property("letter-spacing", "0.14em")
            property("text-transform", "uppercase")
            property("color", "#71717A")
            property("text-align", "center")
            property("margin-bottom", "12px")
        },
        dark = { property("color", "#A1A1AA") },
    )

    rule(".sb-step-rail") {
        property("position", "relative")
        property("display", "grid")
        property("align-items", "center")
        property("height", "40px")
    }
    rule(".sb-step-rail::before") {
        property("content", "''")
        property("position", "absolute")
        property("left", "15%")
        property("right", "15%")
        property("top", "50%")
        property("border-top", "1px dashed #D4D4D8")
        property("transform", "translateY(-0.5px)")
        property("z-index", "0")
        property("pointer-events", "none")
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-step-rail::before", darkMedia) {
        property("border-top-color", "#3F3F46")
    }
    rule("body[data-theme=\"dark\"] .sb-step-rail::before") {
        property("border-top-color", "#3F3F46")
    }
    rule(".sb-step-rail-cell") {
        property("display", "flex")
        property("justify-content", "center")
        property("align-items", "center")
        property("position", "relative")
        property("z-index", "1")
        property("height", "40px")
    }
    rule(".sb-step-rail-node") {
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("width", "28px")
        property("height", "28px")
        property("border-radius", "9999px")
        property("background-color", "#FAFAFA")
        property("border", "1px solid #E4E4E7")
        property("color", "#71717A")
        property("font-family", "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace")
        property("font-size", "12px")
        property("font-weight", "500")
        property(
            "transition",
            Motion.transition("220ms", "background-color", "border-color", "color", "box-shadow", "transform"),
        )
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-step-rail-node", darkMedia) {
        property("background-color", "#0A0A0A")
        property("border-color", "#27272A")
        property("color", "#A1A1AA")
    }
    rule("body[data-theme=\"dark\"] .sb-step-rail-node") {
        property("background-color", "#0A0A0A")
        property("border-color", "#27272A")
        property("color", "#A1A1AA")
    }
    rule(".sb-step-rail-node--active") {
        property("background-color", "#5E6AD2")
        property("border-color", "#5E6AD2")
        property("color", "#FFFFFF")
        property("transform", "scale(1.1)")
        property("box-shadow", "0 0 0 6px rgba(94, 106, 210, 0.12)")
    }
    rule(".sb-step-rail-node--done") {
        property("background-color", "#5E6AD2")
        property("border-color", "#5E6AD2")
        property("color", "#FFFFFF")
    }
    rule(".sb-step-rail-labels") {
        property("display", "grid")
        property("margin-top", "10px")
    }
    rule(".sb-step-rail-label") {
        property("font-family", "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
        property("font-size", "12px")
        property("color", "#71717A")
        property("text-align", "center")
        property("line-height", "1.3")
        property("padding", "0 4px")
        property("white-space", "nowrap")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
    }
    rule(".sb-step-rail-label--active") {
        property("color", "#09090B")
        property("font-weight", "600")
    }
    rule(".sb-step-rail-label--done") {
        property("color", "#5E6AD2")
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-step-rail-label", darkMedia) {
        property("color", "#A1A1AA")
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-step-rail-label--active", darkMedia) {
        property("color", "#FAFAFA")
    }
    rule("body[data-theme=\"dark\"] .sb-step-rail-label") {
        property("color", "#A1A1AA")
    }
    rule("body[data-theme=\"dark\"] .sb-step-rail-label--active") {
        property("color", "#FAFAFA")
    }
}

private fun InitSilkContext.registerSlackMockup() {
    themedMediaGuarded(
        ".sb-slack",
        light = {
            property("background-color", "#FFFFFF")
            property("border", "1px solid #E7E7EA")
            property("border-radius", "10px")
            property("padding", "0")
            property(
                "box-shadow",
                "0 22px 50px -14px rgba(15, 15, 20, 0.28), 0 12px 24px -10px rgba(15, 15, 20, 0.18)",
            )
            property(
                "font-family",
                "'Lato', 'Slack-Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
            )
            property("color", "#1D1C1D")
            property("overflow", "hidden")
        },
        dark = {
            property("background-color", "#1A1D21")
            property("border-color", "#35373B")
            property("color", "#D1D2D3")
            property("box-shadow", "0 30px 60px -20px rgba(0,0,0,0.7)")
        },
    )

    rule(".sb-slack-channel") {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "6px")
        property("padding", "10px 16px")
        property("font-size", "12px")
        property("font-weight", "600")
        property("color", "#1D1C1D")
        property("background-color", "#F8F8F8")
        property("border-bottom", "1px solid #E7E7EA")
    }
    rule(".sb-slack-channel-hash") {
        property("color", "#696A6B")
        property("font-weight", "400")
        property("font-size", "14px")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-channel, body:not([data-theme=\"light\"]) .sb-slack-channel") {
        property("background-color", "#222529")
        property("border-bottom-color", "#35373B")
        property("color", "#D1D2D3")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-channel-hash, body:not([data-theme=\"light\"]) .sb-slack-channel-hash") {
        property("color", "#9B9B9B")
    }

    rule(".sb-slack-row") {
        property("display", "flex")
        property("gap", "10px")
        property("padding", "10px 16px 12px")
    }
    rule(".sb-slack-row:hover") {
        property("background-color", "#F8F8F8")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-row:hover, body:not([data-theme=\"light\"]) .sb-slack-row:hover") {
        property("background-color", "#222529")
    }

    rule(".sb-slack-avatar") {
        property("width", "36px")
        property("height", "36px")
        property("border-radius", "6px")
        property("flex-shrink", "0")
        property("overflow", "hidden")
        property("background-color", "#F4F4F5")
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
    }
    rule(".sb-slack-avatar img") {
        property("width", "100%")
        property("height", "100%")
        property("object-fit", "cover")
        property("display", "block")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-avatar, body:not([data-theme=\"light\"]) .sb-slack-avatar") {
        property("background-color", "#2C2F33")
    }

    rule(".sb-slack-content") {
        property("flex", "1")
        property("min-width", "0")
    }
    rule(".sb-slack-headline") {
        property("display", "flex")
        property("align-items", "baseline")
        property("gap", "8px")
        property("line-height", "1.3")
        property("margin-bottom", "4px")
    }
    rule(".sb-slack-author") {
        property("font-weight", "900")
        property("font-size", "15px")
        property("color", "#1D1C1D")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-author, body:not([data-theme=\"light\"]) .sb-slack-author") {
        property("color", "#D1D2D3")
    }
    rule(".sb-slack-app-badge") {
        property("font-size", "10px")
        property("font-weight", "700")
        property("letter-spacing", "0.04em")
        property("background-color", "#E8E8E8")
        property("color", "#616061")
        property("padding", "1px 5px")
        property("border-radius", "2px")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-app-badge, body:not([data-theme=\"light\"]) .sb-slack-app-badge") {
        property("background-color", "#35373B")
        property("color", "#ABABAD")
    }
    rule(".sb-slack-time") {
        property("font-size", "12px")
        property("color", "#616061")
        property("font-weight", "400")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-time, body:not([data-theme=\"light\"]) .sb-slack-time") {
        property("color", "#9B9B9B")
    }

    rule(".sb-slack-subject") {
        property("font-size", "15px")
        property("font-weight", "700")
        property("line-height", "1.46")
        property("margin-bottom", "10px")
    }

    rule(".sb-slack-person") {
        property("font-size", "15px")
        property("line-height", "1.46")
        property("margin-top", "10px")
    }
    rule(".sb-slack-person:first-of-type") {
        property("margin-top", "0")
    }
    rule(".sb-slack-person-name") {
        property("font-weight", "700")
        property("color", "#1264A3")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-person-name, body:not([data-theme=\"light\"]) .sb-slack-person-name") {
        property("color", "#1D9BD1")
    }
    rule(".sb-slack-line") {
        property("font-size", "15px")
        property("line-height", "1.46")
        property("padding-left", "14px")
        property("position", "relative")
        property("margin-top", "2px")
    }
    rule(".sb-slack-line a") {
        property("color", "${Palette.accent}")
        property("text-decoration", "none")
    }
    rule(".sb-slack-line code") {
        property("font-family", Typography.FONT_MONO)
        property("font-size", "${Typography.caption.size}")
    }
    rule(".sb-slack-line::before") {
        property("content", "'•'")
        property("position", "absolute")
        property("left", "2px")
        property("color", "#696A6B")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-line::before, body:not([data-theme=\"light\"]) .sb-slack-line::before") {
        property("color", "#9B9B9B")
    }
    rule(".sb-slack-line-label") {
        property("font-weight", "700")
        property("color", "#1D1C1D")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-line-label, body:not([data-theme=\"light\"]) .sb-slack-line-label") {
        property("color", "#D1D2D3")
    }

    rule(".sb-slack-divider") {
        property("margin", "14px 0 10px")
        property("border", "none")
        property("border-top", "1px solid #E7E7EA")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-divider, body:not([data-theme=\"light\"]) .sb-slack-divider") {
        property("border-top-color", "#35373B")
    }

    rule(".sb-slack-actions") {
        property("display", "flex")
        property("gap", "8px")
        property("flex-wrap", "wrap")
    }
    rule(".sb-slack-btn") {
        property("font-family", "inherit")
        property("font-size", "14px")
        property("font-weight", "700")
        property("padding", "6px 14px")
        property("border-radius", "4px")
        property("background-color", "#FFFFFF")
        property("border", "1px solid #CDCDCD")
        property("color", "#1D1C1D")
    }
    rule(".sb-slack-btn--primary") {
        property("background-color", "#007A5A")
        property("border-color", "#007A5A")
        property("color", "#FFFFFF")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-btn, body:not([data-theme=\"light\"]) .sb-slack-btn") {
        property("background-color", "#1A1D21")
        property("border-color", "#5F626A")
        property("color", "#D1D2D3")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-btn--primary, body:not([data-theme=\"light\"]) .sb-slack-btn--primary") {
        property("background-color", "#007A5A")
        property("border-color", "#007A5A")
        property("color", "#FFFFFF")
    }

    rule(".sb-slack-mention") {
        property("color", "#1264A3")
        property("font-weight", "500")
        property("background-color", "#E8F1FB")
        property("padding", "0 3px")
        property("border-radius", "3px")
    }
    rule("body[data-theme=\"dark\"] .sb-slack-mention, body:not([data-theme=\"light\"]) .sb-slack-mention") {
        property("color", "#1D9BD1")
        property("background-color", "rgba(29, 155, 209, 0.15)")
    }
}

private fun InitSilkContext.registerGridsAndLayout() {
    rule(".sb-bento") {
        property("display", "grid")
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        property("gap", "16px")
        property("grid-auto-rows", "1fr")
    }
    mediaRule(".sb-bento", CSSMediaQuery.Raw("(max-width: 960px)")) {
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
    }
    mediaRule(".sb-bento > *", CSSMediaQuery.Raw("(max-width: 960px)")) {
        property("grid-column", "span 1 !important")
    }
    mediaRule(".sb-bento", CSSMediaQuery.Raw("(max-width: 640px)")) {
        property("grid-template-columns", "1fr")
    }

    rule(".sb-nav-desktop") {
        property("display", "inline-flex")
    }
    rule(".sb-nav-mobile") {
        property("display", "none")
    }
    rule(".sb-nav-mobile-summary::-webkit-details-marker") {
        property("display", "none")
    }
    rule(".sb-nav-mobile-panel") {
        property("display", "flex")
    }
    rule(".sb-nav-mobile[open] .sb-nav-mobile-summary") {
        property("border-color", "#5E6AD2")
    }
    mediaRule(".sb-nav-desktop", CSSMediaQuery.Raw("(max-width: 640px)")) {
        property("display", "none")
    }
    mediaRule(".sb-nav-mobile", CSSMediaQuery.Raw("(max-width: 640px)")) {
        property("display", "inline-flex")
    }

    rule(".sb-contact-grid") {
        property("display", "grid")
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        property("gap", "24px")
        property("align-items", "stretch")
    }
    mediaRule(".sb-contact-grid", CSSMediaQuery.Raw("(max-width: 820px)")) {
        property("grid-template-columns", "1fr")
    }

    rule(".sb-flow") {
        property("display", "grid")
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        property("gap", "24px")
        property("position", "relative")
    }
    rule(".sb-flow::before") {
        property("content", "''")
        property("position", "absolute")
        property("left", "12%")
        property("right", "12%")
        property("top", "64px")
        property("height", "2px")
        property(
            "background-image",
            "linear-gradient(to right, rgba(94,106,210,0) 0%, rgba(94,106,210,0.35) 20%, rgba(94,106,210,0.35) 80%, rgba(94,106,210,0) 100%)",
        )
        property("z-index", "0")
    }
    mediaRule(".sb-flow", CSSMediaQuery.Raw("(max-width: 820px)")) {
        property("grid-template-columns", "1fr")
    }
    mediaRule(".sb-flow::before", CSSMediaQuery.Raw("(max-width: 820px)")) {
        property("display", "none")
    }
    rule(".sb-flow-node") {
        property("position", "relative")
        property("z-index", "1")
    }

    mediaRule(".sb-a-accent:hover", always) {
        property("color", "#5E6AD2 !important")
    }

    rule(".sb-toc") {
        property("position", "sticky")
        property("top", "96px")
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "6px")
        property("max-height", "calc(100vh - 120px)")
        property("overflow-y", "auto")
    }
    rule(".sb-toc a") {
        property("display", "block")
        property("padding", "6px 10px")
        property("border-radius", "6px")
        property("color", "#52525B")
        property("font-size", "13px")
        property("line-height", "1.35")
        property("transition", "background-color 160ms ease, color 160ms ease")
        property("border-left", "2px solid transparent")
    }
    rule(".sb-toc a:hover") {
        property("color", "#09090B")
        property("background-color", "#F4F4F5")
        property("border-left-color", "#5E6AD2")
    }
    rule("body[data-theme=\"dark\"] .sb-toc a, body:not([data-theme=\"light\"]) .sb-toc a") {
        property("color", "#A1A1AA")
    }
    rule("body[data-theme=\"dark\"] .sb-toc a:hover, body:not([data-theme=\"light\"]) .sb-toc a:hover") {
        property("color", "#FAFAFA")
        property("background-color", "#1F1F22")
    }

    rule(".sb-legal-layout") {
        property("display", "grid")
        property("grid-template-columns", "220px minmax(0, 1fr)")
        property("gap", "64px")
        property("width", "100%")
        property("max-width", "1080px")
        property("margin", "0 auto")
        property("padding", "0 24px")
    }
    mediaRule(".sb-legal-layout", CSSMediaQuery.Raw("(max-width: 900px)")) {
        property("grid-template-columns", "1fr")
        property("gap", "24px")
    }
    mediaRule(".sb-toc", CSSMediaQuery.Raw("(max-width: 900px)")) {
        property("position", "static")
        property("max-height", "none")
    }
    rule(".sb-legal-prose h2") {
        property("font-weight", "${FontWeight.semibold}")
        property("font-size", "28px")
        property("letter-spacing", "-0.03em")
        property("line-height", "1.15")
    }

    mediaRule(".sb-mobile-only", always) {
        property("display", "none !important")
    }
    mediaRule(".sb-desktop-only", CSSMediaQuery.Raw("(max-width: 959px)")) {
        property("display", "none !important")
    }
    mediaRule(".sb-mobile-only", CSSMediaQuery.Raw("(max-width: 959px)")) {
        property("display", "revert !important")
    }
    mediaRule(".sb-nav.sb-mobile-only", CSSMediaQuery.Raw("(max-width: 959px)")) {
        property("display", "flex !important")
    }
    val narrow = CSSMediaQuery.Raw("(max-width: 959px)")
    mediaRule(".sb-settings-layout", narrow) {
        property("flex-direction", "column !important")
        property("align-items", "stretch !important")
        property("gap", "${Spacing.md} !important")
    }
    mediaRule(".sb-settings-sidebar", narrow) {
        property("flex", "0 0 auto !important")
        property("width", "100% !important")
        property("position", "static !important")
        property("max-height", "none !important")
        property("flex-direction", "row !important")
        property("gap", "${Spacing.xxs} !important")
        property("padding-top", "0 !important")
        property("overflow-x", "auto !important")
        property("overflow-y", "visible !important")
    }
    mediaRule(".sb-settings-group-header", narrow) {
        property("display", "none !important")
    }
    mediaRule(".sb-settings-item", narrow) {
        property("flex", "0 0 auto !important")
        property("width", "auto !important")
        property("margin-bottom", "0 !important")
        property("white-space", "nowrap")
    }
}

private fun InitSilkContext.registerAtmosphere() {
    rule(".sb-tracker") {
        property("font-size", "12px")
        property("font-weight", "500")
        property("letter-spacing", "0")
        property("text-transform", "none")
        property("color", "#52525B")
    }
    rule("body[data-theme=\"dark\"] .sb-tracker, body:not([data-theme=\"light\"]) .sb-tracker") {
        property("color", "#A1A1AA")
    }

    rule(".sb-dots") {
        property("background-image", "radial-gradient(rgba(9, 9, 11, 0.08) 1px, transparent 1px)")
        property("background-size", "22px 22px")
        property("background-position", "0 0")
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-dots", darkMedia) {
        property("background-image", "radial-gradient(rgba(255, 255, 255, 0.07) 1px, transparent 1px)")
    }
    rule("body[data-theme=\"dark\"] .sb-dots") {
        property("background-image", "radial-gradient(rgba(255, 255, 255, 0.07) 1px, transparent 1px)")
    }
    rule(".sb-dots-mask") {
        property("-webkit-mask-image", "radial-gradient(ellipse at center, #000 30%, transparent 75%)")
        property("mask-image", "radial-gradient(ellipse at center, #000 30%, transparent 75%)")
    }

    rule(".sb-orb") {
        property("position", "absolute")
        property("border-radius", "9999px")
        property("filter", "blur(80px)")
        property("opacity", "0.55")
        property("pointer-events", "none")
        property("z-index", "0")
        property("will-change", "transform, opacity")
        property("contain", "layout paint")
        property("transform", "translateZ(0)")
    }
    rule(".sb-orb--indigo") {
        property("background", "radial-gradient(circle, rgba(94,106,210,0.55), rgba(94,106,210,0) 70%)")
    }
    rule(".sb-orb--peach") {
        property("background", "radial-gradient(circle, rgba(255,176,120,0.38), rgba(255,176,120,0) 70%)")
    }
    rule(".sb-orb--mint") {
        property("background", "radial-gradient(circle, rgba(120,215,180,0.28), rgba(120,215,180,0) 70%)")
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-orb--indigo", darkMedia) {
        property("opacity", "0.4")
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-orb--peach", darkMedia) {
        property("opacity", "0.22")
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-orb--mint", darkMedia) {
        property("opacity", "0.18")
    }
    rule("body[data-theme=\"dark\"] .sb-orb--indigo") {
        property("opacity", "0.4")
    }
    rule("body[data-theme=\"dark\"] .sb-orb--peach") {
        property("opacity", "0.22")
    }
    rule("body[data-theme=\"dark\"] .sb-orb--mint") {
        property("opacity", "0.18")
    }

    rule(".sb-loader-root") {
        property("position", "fixed")
        property("inset", "0")
        property("width", "100vw")
        property("height", "100vh")
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("background-color", "var(--sb-surface, #FFFFFF)")
        property("z-index", "2200")
        property("animation", "sb-enter-fade 300ms cubic-bezier(0.22, 0.61, 0.36, 1) both")
    }
    rule(".sb-loader-mark") {
        property("width", "72px")
        property("height", "72px")
        property("display", "block")
        property("border-radius", "18px")
    }
    rule(".sb-loader-bar") {
        property("position", "relative")
        property("width", "160px")
        property("height", "2px")
        property("border-radius", "1px")
        property("overflow", "hidden")
        property("background-color", "var(--sb-border-subtle, #F1F1F3)")
    }
    rule(".sb-loader-bar::after") {
        property("content", "\"\"")
        property("position", "absolute")
        property("top", "0")
        property("left", "0")
        property("height", "100%")
        property("width", "40%")
        property("border-radius", "1px")
        property("background-color", "var(--sb-text-primary, #09090B)")
        property("animation", "sb-loader-sweep 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite")
    }
    rule(".sb-loader-caption") {
        property("font-size", "12px")
        property("font-weight", "500")
        property("letter-spacing", "0.08em")
        property("text-transform", "uppercase")
        property("color", "var(--sb-text-tertiary, #A1A1AA)")
        property("font-family", "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace")
    }

    rule(".sb-grain") {
        property("position", "absolute")
        property("inset", "0")
        property("pointer-events", "none")
        property("opacity", "0.035")
        property("mix-blend-mode", "overlay")
        property(
            "background-image",
            "radial-gradient(rgba(0,0,0,1) 1px, transparent 1px), radial-gradient(rgba(0,0,0,1) 1px, transparent 1px)",
        )
        property("background-size", "3px 3px, 5px 5px")
        property("background-position", "0 0, 1px 1px")
    }

    rule(".sb-cta-xl") {
        property("position", "relative")
        property("overflow", "hidden")
        property("isolation", "isolate")
    }
    rule(".sb-cta-xl::before") {
        property("content", "''")
        property("position", "absolute")
        property("inset", "-2px")
        property(
            "background",
            "conic-gradient(from 90deg at 50% 50%, rgba(94,106,210,0.9), rgba(255,176,120,0.6), rgba(120,215,180,0.6), rgba(94,106,210,0.9))",
        )
        property("z-index", "-1")
        property("filter", "blur(24px)")
        property("opacity", "0.55")
        property("transition", "opacity 600ms cubic-bezier(0.22, 0.61, 0.36, 1)")
        property("will-change", "opacity")
    }
    mediaRule(".sb-cta-xl:hover::before", HoverFinePointerMedia) {
        property("opacity", "0.85")
    }

    rule(".sb-section") {
        property("position", "relative")
    }

    rule(".sb-onboarding-backdrop") {
        property("position", "relative")
        property("isolation", "isolate")
        property("min-height", "calc(100vh - 56px)")
    }
    rule(".sb-onboarding-backdrop::before") {
        property("content", "''")
        property("position", "absolute")
        property("inset", "0")
        property("z-index", "-1")
        property("background-image", "radial-gradient(rgba(9, 9, 11, 0.06) 1px, transparent 1px)")
        property("background-size", "26px 26px")
        property(
            "-webkit-mask-image",
            "radial-gradient(ellipse 70% 50% at 50% 30%, #000 20%, transparent 80%)",
        )
        property(
            "mask-image",
            "radial-gradient(ellipse 70% 50% at 50% 30%, #000 20%, transparent 80%)",
        )
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-onboarding-backdrop::before", darkMedia) {
        property("background-image", "radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px)")
    }
    rule("body[data-theme=\"dark\"] .sb-onboarding-backdrop::before") {
        property("background-image", "radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px)")
    }
    rule(".sb-page-accent-strip") {
        property("position", "absolute")
        property("top", "0")
        property("left", "0")
        property("right", "0")
        property("height", "3px")
        property("z-index", "1")
        property(
            "background-image",
            "linear-gradient(to right, rgba(94, 106, 210, 0) 0%, rgba(94, 106, 210, 0.5) 30%, rgba(94, 106, 210, 0.5) 70%, rgba(94, 106, 210, 0) 100%)",
        )
    }
}

private fun InitSilkContext.registerMotionAndTypography() {
    rule(".sb-serif") {
        property("font-family", "'Instrument Serif', 'Iowan Old Style', 'Palatino', Georgia, serif")
        property("font-weight", "400")
        property("letter-spacing", "-0.01em")
    }
    rule(".sb-serif-italic") {
        property("font-family", "'Instrument Serif', 'Iowan Old Style', 'Palatino', Georgia, serif")
        property("font-style", "italic")
        property("font-weight", "400")
    }

    rule(".sb-float") {
        property("animation", "sb-float 7s ease-in-out infinite")
    }

    rule(".sb-display-lg") {
        property("font-family", Typography.displayLg.fontFamily)
        property("font-size", "${Typography.displayLg.size}")
        property("font-weight", "${Typography.displayLg.weight}")
        property("line-height", "${Typography.displayLg.lineHeight}")
        property("letter-spacing", "-0.02em")
    }
    rule(".sb-display-md") {
        property("font-family", Typography.displayMd.fontFamily)
        property("font-size", "${Typography.displayMd.size}")
        property("font-weight", "${Typography.displayMd.weight}")
        property("line-height", "${Typography.displayMd.lineHeight}")
        property("letter-spacing", "-0.02em")
    }
    rule(".sb-display-sm") {
        property("font-family", Typography.displaySm.fontFamily)
        property("font-size", "${Typography.displaySm.size}")
        property("font-weight", "${Typography.displaySm.weight}")
        property("line-height", "${Typography.displaySm.lineHeight}")
        property("letter-spacing", "-0.02em")
    }
    rule(".sb-mono-muted-italic") {
        property("font-family", "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace")
        property("font-size", "15px")
        property("font-style", "italic")
        property("font-weight", "400")
        property("line-height", "1.5")
        property("color", "#52525B")
    }
    mediaRule("body:not([data-theme=\"light\"]) .sb-mono-muted-italic", darkMedia) {
        property("color", "#A1A1AA")
    }
    rule("body[data-theme=\"dark\"] .sb-mono-muted-italic") {
        property("color", "#A1A1AA")
    }
    rule(".sb-stat-value") {
        property("font-family", Typography.statValue.fontFamily)
        property("font-size", "${Typography.statValue.size}")
        property("font-weight", "${Typography.statValue.weight}")
        property("line-height", "${Typography.statValue.lineHeight}")
        property("letter-spacing", "-0.03em")
        property("font-variant-numeric", "tabular-nums")
    }
    rule(".sb-mono-quote") {
        property("font-family", "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace")
        property("font-size", "20px")
        property("font-weight", "500")
        property("line-height", "1.5")
        property("letter-spacing", "-0.005em")
        property("display", "flex")
        property("gap", "12px")
        property("align-items", "baseline")
    }
    rule(".sb-mono-quote::before") {
        property("content", "'>'")
        property("color", "#5E6AD2")
        property("font-weight", "600")
        property("flex-shrink", "0")
    }

    rule(".sb-pulse") {
        property("animation", "sb-pulse 2s ease-out infinite")
    }
    rule(".sb-accent-pulse") {
        property("animation", "sb-accent-pulse 2.2s cubic-bezier(0.22, 0.61, 0.36, 1) infinite")
    }

    rule(".sb-enter-fade-up") {
        property("animation", "sb-enter-fade-up ${Motion.ENTER_DUR} ${Motion.EASE_STANDARD} backwards")
    }
    rule(".sb-enter-fade") {
        property("animation", "sb-enter-fade 300ms cubic-bezier(0.22, 0.61, 0.36, 1) both")
    }

    rule(".sb-badge-morph") {
        property("transition", Motion.transition(Motion.DUR_BASE, "background-color", "color", "border-color"))
    }

    rule(".sb-nav-row") {
        property("position", "relative")
        property("transition", Motion.transition(Motion.DUR_FAST, "background-color", "color", "transform"))
    }
    rule(".sb-nav-indicator") {
        property("position", "absolute")
        property("left", "0")
        property("top", "8px")
        property("bottom", "8px")
        property("width", "3px")
        property("border-radius", "2px")
        property("background-color", "var(--sb-accent, #5E6AD2)")
        property("pointer-events", "none")
        property("z-index", "1")
    }
}

private fun InitSilkContext.presenceSurface(
    selector: String,
    properties: List<String>,
    durationIn: String,
    durationOut: String,
    curveIn: String,
    curveOut: String = curveIn,
    hidden: StyleScope.() -> Unit,
    shown: StyleScope.() -> Unit,
) {
    val propertyList = properties.toTypedArray()
    rule(selector) {
        hidden()
        property("transition", Motion.transitionWith(durationOut, curveOut, *propertyList))
    }
    rule("$selector.${Motion.OPEN_CLASS}") {
        shown()
        property("transition", Motion.transitionWith(durationIn, curveIn, *propertyList))
    }
}

private fun InitSilkContext.registerPresenceSurfaces() {
    presenceSurface(
        selector = ".sb-pop",
        properties = listOf("opacity", "transform"),
        durationIn = Motion.DUR_POPOVER_IN,
        durationOut = Motion.DUR_POPOVER_OUT,
        curveIn = Motion.EASE_EMPHASIS,
        hidden = {
            property("opacity", "0")
            property("transform", "scale(${Motion.POPOVER_SCALE})")
            property("pointer-events", "none")
        },
        shown = {
            property("opacity", "1")
            property("transform", "scale(1)")
            property("pointer-events", "auto")
        },
    )

    registerTooltipSurface()

    presenceSurface(
        selector = ".sb-scrim",
        properties = listOf("opacity"),
        durationIn = Motion.DUR_MODAL_IN,
        durationOut = Motion.DUR_MODAL_OUT,
        curveIn = Motion.EASE_STANDARD,
        hidden = {
            property("opacity", "0")
            property("pointer-events", "none")
        },
        shown = {
            property("opacity", "1")
            property("pointer-events", "auto")
        },
    )
    presenceSurface(
        selector = ".sb-dialog",
        properties = listOf("opacity", "transform"),
        durationIn = Motion.DUR_MODAL_IN,
        durationOut = Motion.DUR_MODAL_OUT,
        curveIn = Motion.EASE_EMPHASIS,
        hidden = {
            property("opacity", "0")
            property("transform", "scale(${Motion.MODAL_SCALE})")
        },
        shown = {
            property("opacity", "1")
            property("transform", "scale(1)")
        },
    )
    rule(".sb-ghost-out") {
        property("pointer-events", "none")
        property("animation", "sb-fade-out ${Motion.DUR_MODAL_OUT} ${Motion.EASE_STANDARD} both")
    }
    rule(".sb-ghost-out .sb-dialog") {
        property("animation", "sb-dialog-out ${Motion.DUR_MODAL_OUT} ${Motion.EASE_EMPHASIS} both")
    }

    presenceSurface(
        selector = ".sb-drawer-scrim",
        properties = listOf("opacity"),
        durationIn = Motion.DUR_SCRIM,
        durationOut = Motion.DUR_SCRIM,
        curveIn = Motion.EASE_STANDARD,
        hidden = {
            property("opacity", "0")
            property("pointer-events", "none")
        },
        shown = {
            property("opacity", "1")
            property("pointer-events", "auto")
        },
    )
    presenceSurface(
        selector = ".sb-drawer",
        properties = listOf("transform"),
        durationIn = Motion.DUR_DRAWER_IN,
        durationOut = Motion.DUR_DRAWER_OUT,
        curveIn = Motion.EASE_DRAWER,
        hidden = { property("transform", "translateX(-100%)") },
        shown = { property("transform", "translateX(0)") },
    )

    presenceSurface(
        selector = ".sb-toast",
        properties = listOf("opacity", "transform"),
        durationIn = "${Motion.TOAST_IN_MS}ms",
        durationOut = "${Motion.TOAST_OUT_MS}ms",
        curveIn = Motion.EASE_EMPHASIS,
        curveOut = Motion.EASE_STANDARD,
        hidden = {
            property("opacity", "0")
            property("transform", "translate(-50%, ${Motion.TOAST_OFFSET_PX}px)")
        },
        shown = {
            property("opacity", "1")
            property("transform", "translate(-50%, 0)")
        },
    )

    presenceSurface(
        selector = ".sb-disclosure",
        properties = listOf("grid-template-rows", "opacity"),
        durationIn = Motion.DUR_DISCLOSURE,
        durationOut = Motion.DUR_DISCLOSURE,
        curveIn = Motion.EASE_STANDARD,
        hidden = {
            property("display", "grid")
            property("grid-template-rows", "0fr")
            property("opacity", "0")
        },
        shown = {
            property("grid-template-rows", "1fr")
            property("opacity", "1")
        },
    )
    rule(".sb-disclosure-inner") {
        property("min-height", "0")
        property("overflow", "hidden")
    }
    rule(".sb-disclosure-inner.sb-settled") {
        property("overflow", "visible")
    }

    presenceSurface(
        selector = ".sb-icon-swap",
        properties = listOf("opacity", "transform"),
        durationIn = Motion.DUR_ICON_SWAP,
        durationOut = Motion.DUR_ICON_SWAP,
        curveIn = Motion.EASE_STANDARD,
        hidden = {
            property("opacity", "0")
            property("transform", "scale(${Motion.ICON_SWAP_SCALE})")
        },
        shown = {
            property("opacity", "1")
            property("transform", "scale(1)")
        },
    )
    presenceSurface(
        selector = ".sb-fade-layer",
        properties = listOf("opacity"),
        durationIn = Motion.DUR_FAST,
        durationOut = Motion.DUR_FAST,
        curveIn = Motion.EASE_STANDARD,
        hidden = { property("opacity", "0") },
        shown = { property("opacity", "1") },
    )
}

private fun InitSilkContext.registerTooltipSurface() {
    val tooltipTransition = { duration: String ->
        Motion.transitionWith(duration, Motion.EASE_EMPHASIS, "opacity", "transform", "visibility")
    }
    val shownDeclarations: StyleScope.() -> Unit = {
        property("opacity", "1")
        property("visibility", "visible")
        property("transform", "none")
        property("transition", tooltipTransition(Motion.DUR_POPOVER_IN))
    }
    rule(".sb-tip") {
        property("opacity", "0")
        property("visibility", "hidden")
        property("transform", "var(--sb-tip-from)")
        property("transition", tooltipTransition(Motion.DUR_POPOVER_OUT))
    }
    val offset = "${Motion.TOOLTIP_OFFSET_PX}px"
    val scale = "scale(${Motion.TOOLTIP_SCALE})"
    rule(".sb-tip-top") {
        property("--sb-tip-from", "translateY($offset) $scale")
        property("transform-origin", "bottom center")
    }
    rule(".sb-tip-bottom") {
        property("--sb-tip-from", "translateY(-$offset) $scale")
        property("transform-origin", "top center")
    }
    rule(".sb-tip-left") {
        property("--sb-tip-from", "translateX($offset) $scale")
        property("transform-origin", "center right")
    }
    rule(".sb-tip-right") {
        property("--sb-tip-from", "translateX(-$offset) $scale")
        property("transform-origin", "center left")
    }
    rule(".sb-tip.${Motion.OPEN_CLASS}", shownDeclarations)
    rule(".sb-tooltip-host:hover .sb-tip, .sb-tooltip-host:focus-within .sb-tip", shownDeclarations)
}

private fun InitSilkContext.registerPressAndFeedback() {
    val feedbackProperties = arrayOf("background-color", "color", "border-color", "box-shadow", "opacity")
    val feedback = Motion.transition(Motion.DUR_FAST, *feedbackProperties)
    rule(".${Motion.PRESSABLE_CLASS}.${Motion.PRESSABLE_CLASS}") {
        property("transition", "$feedback, transform ${Motion.DUR_RELEASE} ${Motion.EASE_STANDARD}")
    }
    rule(".${Motion.PRESSABLE_CLASS}.${Motion.PRESSABLE_CLASS}:active") {
        property("transform", "scale(var(${Motion.PRESS_SCALE_VAR}, ${Motion.PRESS_SCALE}))")
        property("transition", "$feedback, transform ${Motion.DUR_PRESS} ${Motion.EASE_STANDARD}")
    }

    rule(".sb-seg-thumb") {
        property("transition", Motion.transitionWith(Motion.DUR_SEGMENT_THUMB, Motion.EASE_EMPHASIS, "transform"))
    }
    rule(".sb-tab-underline") {
        property("transform-origin", "0 0")
        property("transition", Motion.transition(Motion.DUR_BASE, "transform"))
    }
    rule(".sb-chevron") {
        property("display", "inline-flex")
        property("transition", Motion.transition(Motion.DUR_CHEVRON, "transform"))
    }
    rule(".sb-chevron.${Motion.OPEN_CLASS}") {
        property("transform", "rotate(180deg)")
    }

    rule(".sb-content-swap") {
        property("animation", "sb-enter-fade ${Motion.DUR_CONTENT_SWAP} ${Motion.EASE_STANDARD} both")
    }
    rule(".sb-state-swap") {
        property("animation", "sb-enter-fade-up ${Motion.DUR_CONTENT_SWAP} ${Motion.EASE_STANDARD} both")
    }
    rule(".sb-check-draw path") {
        property("stroke-dasharray", "${Motion.CHECK_DASH_LENGTH}")
        property("animation", "sb-check-draw ${Motion.DUR_CHECK_DRAW} ${Motion.EASE_EMPHASIS} both")
    }
}
